import { Leaf } from 'lucide-react';

import { Text } from '@/components/text';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { UNIT_CO2 } from '@/lib/constants';
import { Co2Emission, EmissionsDetailBreakdownDataSource } from '@/lib/models/emissions/types';
import { EmissionsModeBreakdown, EmissionStats } from '@/types';
import { FuelBurnUnitType, SegmentModeType } from '@/types/api-types';
import { formatEmissionsNum } from '@/utils/format/emissions';
import { calcMinMaxPercent } from '@/utils/helpers/maths';
import { getEmissionsLevelString } from '@/utils/strings';
import { tv } from '@/utils/styles';

import { EmissionsDetailBreakdown } from '../detailed-breakdown';

export interface EmissionsIndicatorProps {
  mode: SegmentModeType;
  value?: number;
  unit?: FuelBurnUnitType;
  comparison?: EmissionStats;
  modeBreakdown?: EmissionsModeBreakdown;
  wtwBreakdown?: Co2Emission;
  averageMsg?: string;
  isLocked?: boolean;
  canOpenInCalculator?: boolean;
  // Raw data for transformation, only used when canOpenInCalculator is true
  source?: EmissionsDetailBreakdownDataSource;
}

const EmissionsIndicator = ({
  mode,
  value,
  unit,
  comparison,
  modeBreakdown,
  wtwBreakdown,
  averageMsg,
  isLocked,
  canOpenInCalculator,
  source,
}: EmissionsIndicatorProps) => {
  const {
    base,
    bg,
    content,
    statusBar,
    statusBarWrapper,
    indicator,
    primaryText,
    textUnderline,
    subTextWrapper,
    subText,
  } = styles();

  if (!value)
    return (
      <div className={base()}>
        <Text variant="body" className={primaryText({ className: 'text-grey-400' })}>
          -.-- {UNIT_CO2}
        </Text>
        <div className={subTextWrapper()}>
          <Text variant="sm" className={subText()}>
            Emissions N/A
          </Text>
        </div>
      </div>
    );

  const percentage = comparison ? calcMinMaxPercent(value, comparison.max, comparison.min) : 50;
  const rangeString = getEmissionsLevelString(percentage);

  return (
    <div className={base()}>
      <span className={bg()} />
      <HoverCard openDelay={300}>
        <div className={content()}>
          <div className={statusBar()}>
            <span className={statusBarWrapper()}>
              <span className={indicator()} style={{ bottom: `${percentage}%` }} />
            </span>
          </div>
          <HoverCardTrigger>
            <Text variant="body" className={primaryText()}>
              <span className={textUnderline()}>
                {formatEmissionsNum(value)} <small>{UNIT_CO2}</small>
              </span>
            </Text>
            <div className={subTextWrapper()}>
              {percentage < 5 && <Leaf size={14} className="mr-1 fill-green-300" />}
              <Text variant="sm" className={subText()}>
                {rangeString}
              </Text>
            </div>
          </HoverCardTrigger>
          <HoverCardContent>
            <EmissionsDetailBreakdown
              mode={mode}
              value={value}
              unit={unit}
              comparison={comparison}
              modeBreakdown={modeBreakdown}
              wtwBreakdown={wtwBreakdown}
              percentage={percentage}
              averageMsg={averageMsg}
              isLocked={isLocked}
              isInCard
              canOpenInCalculator={canOpenInCalculator}
              source={source}
            />
          </HoverCardContent>
        </div>
      </HoverCard>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'group/indicator relative',
    content: 'relative z-2 pl-4 leading-[14px]',
    bg: 'absolute -bottom-1.5 -left-2 -top-1.5 right-0 z-1 rounded-md group-hover/indicator:bg-grey-50',
    statusBar: 'absolute bottom-0.5 left-0 top-0.5 w-2 rounded bg-gradient-to-b from-error via-yellowDark to-green-300',
    statusBarWrapper: 'absolute bottom-1 left-0 right-0 top-1',
    indicator:
      'absolute -left-[5px] -mb-xs h-0 w-0 border-b-4 border-l-[5px] border-t-4 border-b-transparent border-l-grey-700 border-t-transparent',
    primaryText: 'm-0 whitespace-nowrap text-baseSm text-text-primary',
    subTextWrapper: 'mt-2 flex items-center',
    subText: 'm-0 whitespace-nowrap text-xs text-text-secondary',
    textUnderline:
      'relative after:absolute after:-bottom-0.5 after:left-0 after:right-0 after:border-b after:border-dashed after:border-grey-300 after:content-[""]',
  },
});

export { EmissionsIndicator };
